<template>
  <div
    ref="inputWrapper"
    class="relative z-10 flex items-center w-full gap-2 p-2 transition-all duration-200 border border-transparent rounded-md max-h-10 text-gray bg-platinum"
    :class="{'mt-6': label && label !== ''}"
    :style="(disabled ? {pointerEvents: 'none'} : {}, {marginBottom: errHeight + 'px'})"
  >
    <div v-if="label && label !== ''" class="absolute left-0 -top-6">
      <t-text :size="'sm'" class="font-semibold">{{ label }}</t-text>
    </div>
    <div class="text-inherit">
      <slot name="left-side"></slot>
    </div>
    <input
      ref="input"
      :disabled="disabled"
      v-model="inputVal"
      :value="value ? value : ''"
      @input="input()"
      @focus="focus()"
      @blur="blur()"
      @change="change"
      @keyup="$emit('keyup')"
      :type="inputType"
      :placeholder="placeholder"
      :min="min"
      :max="max"
      :step="step"
      :inputmode="inputmode"
      :autocomplete="autocomplete"
      class="w-full leading-5 text-16 text-inherit placeholder:text-inherit bg-platinum"
      :class="{'text-gray': disabled}"
    />
    <div ref="rightSide" class="absolute cursor-pointer right-2 bottom-2 text-inherit">
      <slot name="right-side"></slot>
    </div>
    <t-text
      :size="'md'"
      v-show="handleErrors"
      ref="error"
      class="absolute left-0 ml-2"
      :color="'red'"
      :style="{bottom: `-${errHeight}px`}"
      >{{ fieldError }}</t-text
    >
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  export default {
    name: 'dark-sm-input',
    props: {
      label: String,
      placeholder: {
        type: String,
        required: true
      },
      pattern: {
        type: String,
        default: ''
      },
      inputType: String,
      inputColor: {
        type: String,
        required: false,
        default: 'rgb(255, 255, 255)'
      },
      autocomplete: String,
      value: String,
      min: [String, Number],
      max: [String, Number],
      step: [String, Number],
      handleErrors: {
        type: Boolean,
        required: false,
        default: true
      },
      disabled: Boolean,
      preventAutoFocus: Boolean,
      inputmode: {
        type: String,
        validator: v => ['none', 'numeric', 'tel', 'decimal', 'email', 'url', 'search'].indexOf(v) !== -1
      }
    },
    data() {
      return {
        inputVal: this.value ? this.value : '',
        interval: null,
        fieldError: '',
        errHeight: 0,
        isValid: true
      }
    },
    async mounted() {
      await this.componentLoaded()

      this.$refs.input.style.paddingRight = this.$refs.rightSide.clientWidth + 'px'
      if (this.inputType === 'number') {
        this.$refs.input.addEventListener('keypress', e => this.secureNumberInput(e))
      }

      if (!this.preventAutoFocus && this.$refs.input?.value !== '') this.focus()

      setTimeout(() => {
        if (!this.preventAutoFocus && this.$refs.input?.value !== '') this.focus()
      }, 100)

      this.interval = setInterval(() => {
        let style = window.getComputedStyle(this.$refs.input)

        if (style && (style.backgroundColor !== this.inputColor || style.backgroundImage !== 'none')) {
          this.$emit('isAutofilled')
          clearInterval(this.interval)
        }
      }, 333)
    },
    methods: {
      secureNumberInput(e) {
        if ((e.which !== 8 && e.which !== 0 && e.which !== 13 && e.which < 48) || e.which > 57) {
          e.preventDefault()
        }
      },
      async componentLoaded() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (this.$refs.input && this.$refs.inputWrapper && this.$refs.error && this.$refs.rightSide) {
              clearInterval(interval)
              resolve()
            }
          }, 10)
        })
      },
      input() {
        if (this.pattern !== '') {
          const regex = new RegExp(this.pattern.replace(/'/g, ''))
          if (!regex.test(this.getValue())) {
            this.isValid = false
          } else {
            this.clearError()
          }
        }
        this.$emit('input', this.getValue())
      },
      change($event) {
        const value = $event.target.value

        if (this.inputType === 'number' && this.min !== null && this.max !== null && this.min >= 0 && this.max >= 0) {
          const notEnough = value < this.min
          const tooMuch = value > this.max
          const err = notEnough ? `The minimum value is ${this.min}!` : tooMuch ? `The maximum value is ${this.max}!` : ''

          if (err) this.$toast.error(err)

          $event.target.value = notEnough ? +this.min : tooMuch ? +this.max : value
        } else {
          $event.target.value = value
        }

        this.$emit('change', $event.target.value)
      },
      focus() {
        if (!this.$refs.inputWrapper) return
        this.$refs.inputWrapper.classList.replace('text-gray', 'text-black')
        this.$emit('focus')
      },
      blur() {
        if (!this.$refs.inputWrapper) return
        this.$refs.inputWrapper.classList.replace('text-black', 'text-gray')
        this.$emit('blur')
      },
      getValue() {
        return this.$refs.input?.value
      },
      setError(err) {
        const span = document.createElement('span')
        span.textContent = err
        span.style.cssText = 'word-break: break-word'
        const height = this.$helpers.getNodeHeight([span], this.$refs.input.clientWidth)
        this.$refs.error.$el.style.bottom = 0 - height + 'px'
        this.$refs.error.$el.style.maxWidth = this.$refs.input.clientWidth + 'px'
        this.$refs.inputWrapper.style.marginBottom = height + 'px'
        this.$refs.inputWrapper.classList.add('not-valid')
        this.fieldError = err
        setTimeout(() => {
          this.errHeight = this.$refs.error.$el.clientHeight + 8
        }, 0)
        this.isValid = false
      },
      hasError() {
        return this.fieldError !== ''
      },
      clearError() {
        this.fieldError = ''
        this.errHeight = 0
        this.$refs.inputWrapper.style.marginBottom = ''
        this.$refs.inputWrapper.classList.remove('not-valid')
        this.isValid = true
      }
    },
    computed: {
      ...mapGetters(['themeColorRGBA'])
    },
    watch: {
      value(newVal) {
        this.inputVal = newVal
        if (!this.preventAutoFocus && (this.inputVal !== null || this.inputVal !== '')) this.focus()
        if (this.$refs.inputWrapper.classList.contains('disabled-input') && (this.inputVal == null || this.inputVal == '')) this.blur()
      },
      isValid(newVal) {
        if (newVal) {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-red', 'border-transparent'], 'border-green')
            this.$refs.label.classList.replace(['text-red', 'text-gray'], 'text-green')
          }, 0)
        } else {
          setTimeout(() => {
            this.$refs.inputWrapper.classList.replace(['border-green', 'border-transparent'], 'border-red')
            this.$refs.label.classList.replace(['text-gray', 'text-green'], 'text-red')
          }, 0)
        }
      }
    },
    beforeDestroy() {
      if (this.inputType === 'number') {
        this.$refs.input.removeEventListener('keypress', e => this.secureNumberInput(e))
      }
    },
    destroyed() {
      clearInterval(this.interval)
    }
  }
</script>
